import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  BASE_URL,
  getLocalStorageValue,
  getUserId,
} from "../../components/Helpers/helpers";

export const spendApi = createApi({
  reducerPath: "spendApi",

  baseQuery: fetchBaseQuery({
    baseUrl: BASE_URL,
  }),

  tagTypes: ["Spend", "SpendDetail", "SpendStatus"],

  endpoints: (builder) => ({
    getSpendList: builder.query({
      query: (params = {}) => {
        const OrganisationID = getLocalStorageValue("OrganisationID");
        const currentUser = getUserId();

        const baseParams = {
          OrganisationID,
          page: params.page || 1,
          role_name: currentUser?.Role?.Name || "User",
          UserId: currentUser?.UserID,
          MinDate: params.MinDate,
          MaxDate: params.MaxDate,
          Reference: params.Description,
          user_status: params.user_status,
          bk_status: params.bk_status,
          Collaborator: params.Collaborator,
          Spend: params.Spend,
          Receive: params.Receive,
          AccountID: params.AccountID,
          limit: params.limit || 10,
          Description: params.Description,
          latestRecords: params.latestRecords,
          search: params.search,
          CurrencyCode: params.CurrencyCode,
        };

        const queryParams = { ...baseParams };

        if (params.filters && Array.isArray(params.filters)) {
          const cleanedFilters = params.filters.filter((filter) => {
            if (!filter || !filter.name) return false;

            if (
              filter.value === "" ||
              filter.value === null ||
              filter.value === undefined
            ) {
              return false;
            }

            if (
              typeof filter.value === "object" &&
              !Array.isArray(filter.value)
            ) {
              return Object.keys(filter.value).length > 0;
            }

            return true;
          });

          if (cleanedFilters.length > 0) {
            queryParams.filters = JSON.stringify(cleanedFilters);
          }
        }

        if (params.sort && Array.isArray(params.sort)) {
          const cleanedSort = params.sort.filter(
            (s) => s && s.field && s.order,
          );

          if (cleanedSort.length > 0) {
            queryParams.sort = JSON.stringify(cleanedSort);
          }
        }

        Object.keys(queryParams).forEach((key) => {
          if (
            queryParams[key] === "" ||
            queryParams[key] === null ||
            queryParams[key] === undefined
          ) {
            delete queryParams[key];
          }
        });

        const queryString = new URLSearchParams(queryParams).toString();

        return `/spend?${queryString}`;
      },

      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,

      providesTags: ["Spend", "SpendDetail"],
    }),

    getAllSpendList: builder.query({
      query: (params = {}) => {
        const OrganisationID = getLocalStorageValue("OrganisationID");
        const currentUser = getUserId();

        const baseParams = {
          OrganisationID,
          page: params.page || 1,
          role_name: currentUser?.Role?.Name || "User",
          UserId: currentUser?.UserID,
        };

        const queryParams = { ...baseParams };

        if (params.filters && Array.isArray(params.filters)) {
          const cleanedFilters = params.filters.filter(
            (f) =>
              f?.name &&
              f?.value !== "" &&
              f?.value !== null &&
              f?.value !== undefined,
          );

          if (cleanedFilters.length) {
            queryParams.filters = JSON.stringify(cleanedFilters);
          }
        }

        if (params.sort && Array.isArray(params.sort)) {
          const cleanedSort = params.sort.filter(
            (s) => s && s.field && s.order,
          );

          if (cleanedSort.length) {
            queryParams.sort = JSON.stringify(cleanedSort);
          }
        }

        const queryString = new URLSearchParams(queryParams).toString();

        return `/spend?${queryString}`;
      },

      keepUnusedDataFor: 0,
      refetchOnMountOrArgChange: true,
      refetchOnFocus: true,

      providesTags: ["Spend", "SpendDetail"],
    }),

    getSpendDetail: builder.query({
      query: (id) => `/spend/spend-detail/${id}`,
      providesTags: (result, error, id) => [{ type: "SpendDetail", id }],
    }),

    updateSpendUser: builder.mutation({
      query: ({ BankTransactionID, UserID, CurrentLoginedUserId }) => ({
        url: `/spend/update-spend-user`,
        method: "POST",
        body: { BankTransactionID, UserID, CurrentLoginedUserId },
      }),
      invalidatesTags: ["Spend", "SpendDetail"],
    }),

    // 🔹 Approve Spend
    approveSpend: builder.mutation({
      query: ({ BankTransactionID, currentUserId }) => ({
        url: `/spend/update-spend-status`,
        method: "POST",
        body: { BankTransactionID, currentUserId },
      }),
      invalidatesTags: ["Spend", "SpendDetail"],
    }),

    verifySpend: builder.mutation({
      query: ({ BankTransactionID, currentUserId }) => ({
        url: `/spend/verified-spend`,
        method: "POST",
        body: { BankTransactionID, currentUserId },
      }),
      invalidatesTags: ["Spend", "SpendDetail"],
    }),

    // 🔹 Delete Spend
    deleteSpend: builder.mutation({
      query: (BankTransactionID) => ({
        url: `/spend/delete-spend-document`,
        method: "POST",
        body: { BankTransactionID },
      }),
      invalidatesTags: ["Spend"],
    }),

    postSpendAttachment: builder.mutation({
      query: ({ BankTransactionID, OrganisationID, FileName }) => ({
        url: `/spend/${BankTransactionID}/Attachments/${FileName}`,
        method: "POST",
        body: { BankTransactionID, OrganisationID, fileName: FileName },
      }),
      invalidatesTags: ["Spend", "SpendDetail"],
    }),

    readSpendStatus: builder.mutation({
      query: (id) => ({
        url: `/spend/read-spend-status/${id}`,
        method: "POST",
      }),
      invalidatesTags: ["SpendStatus"],
    }),

    updateSpendDetails: builder.mutation({
      query: (payload) => ({
        url: `/spend/spendUpdates`,
        method: "POST",
        body: payload,
      }),
      invalidatesTags: ["Spend", "SpendDetail"],
    }),

    getLastBKUser: builder.query({
      query: ({ organisationId, BankTransactionID }) => ({
        url: "/spend/last-bk-user",
        params: { organisationId, BankStatementID: BankTransactionID },
      }),
    }),
  }),
});

export const {
  useGetSpendListQuery,
  useGetAllSpendListQuery,
  useGetSpendDetailQuery,
  useUpdateSpendUserMutation,
  useApproveSpendMutation,
  useVerifySpendMutation,
  useDeleteSpendMutation,
  usePostSpendAttachmentMutation,
  useReadSpendStatusMutation,
  useUpdateSpendDetailsMutation,
  useGetLastBKUserQuery,
} = spendApi;
